import React, { SetStateAction, useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { GlobalStyles, Toaster } from '@octane/spark'
import LoadingSpinner from 'components/LoadingSpinner'
import DealerRedirector from 'containers/DealerRedirector/DealerRedirector'
import DocumentViewerManager from 'containers/DocumentViewerManager'
import useAuthSessionSync from 'hooks/useAuthSessionSync'
import useDataDogRUM from 'hooks/useDataDogRUM'
import useGoogleTagManager from 'hooks/useGoogleTagManager'
import useKeepSessionAlive from 'hooks/useKeepSessionAlive'

import AppRoutes, { RouteType } from './AppRoutes'
import Footer from './components/Footer'
import ScrollToTop from './components/ScrollToTop'
import DashboardManager from './containers/DashboardManager'
import IntercomManager from './containers/IntercomManager'
import LoginManager from './containers/LoginManager/LoginManager'
import { BrandThemeProvider } from './contexts/BrandThemeProvider'
import useUser from './hooks/useUser'
import './App.css'
import { OctaneLogger } from './integrations/datadog/OctaneLogger'
import LoadingStatus from './state/enums/LoadingStatus'
import { isPublicRoute } from './utils/isPublicRoute'

export type AppContainerProps = {
    readonly routeOverrides?: RouteType[]
    readonly children?: React.ReactNode
}
function AppContainer({
    routeOverrides,
    children,
}: AppContainerProps): JSX.Element {
    const { pathname } = useLocation()
    const navigate = useNavigate()
    const { isLoggedIn, status, user } = useUser()
    const googleTagManager = useGoogleTagManager()
    const { ddRUM } = useDataDogRUM()

    const [redirectState, setRedirectState] = useState<boolean | undefined>(
        undefined,
    )

    const handleRedirectStateChange = (
        state: SetStateAction<boolean | undefined>,
    ) => {
        setRedirectState(state)
    }

    useAuthSessionSync(isLoggedIn === true)
    useKeepSessionAlive()

    useEffect(() => {
        if (user) {
            ddRUM?.setUser(user)
            OctaneLogger.setUser(user)
        } else {
            ddRUM?.clearUser()
            OctaneLogger.clearUser()
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id])

    useEffect(() => {
        ddRUM?.startView(pathname.replace('!', ''))
    }, [pathname, ddRUM])

    useEffect(() => {
        googleTagManager.trackPageview(user?.id)
    }, [pathname, googleTagManager, user?.id])

    const isLoggedOutAtProtectedRoute =
        !isLoggedIn &&
        (status === LoadingStatus.LOADED || status === LoadingStatus.FAILED) &&
        !isPublicRoute(pathname)

    useEffect(() => {
        // Don't redirect legacy hashbang routes (e.g. /!/partner/lightspeed) —
        // the legacy iframe needs to load at the original URL to handle partner auth.
        // See the same guard in ProtectedRoutes.tsx for context.
        if (
            isLoggedOutAtProtectedRoute &&
            pathname !== '/' &&
            !pathname.startsWith('/!/')
        ) {
            window.sessionStorage.setItem('previousPath', pathname)
            navigate('/', { replace: true })
        }
    }, [isLoggedOutAtProtectedRoute, pathname, navigate])

    return (
        <>
            <GlobalStyles />
            <ScrollToTop />
            <BrandThemeProvider>
                <Toaster />
                {children}

                <LoginManager />
                {isLoggedIn && (
                    <>
                        <DocumentViewerManager />
                        <IntercomManager />
                        <DashboardManager />
                    </>
                )}
                <DealerRedirector
                    onRedirectStateChange={handleRedirectStateChange}
                />
                <main>
                    {isLoggedIn &&
                    (redirectState === undefined || redirectState) ? (
                        <LoadingSpinner />
                    ) : isLoggedOutAtProtectedRoute ? null : (
                        <AppRoutes routeOverrides={routeOverrides} />
                    )}
                </main>
                <Footer />
            </BrandThemeProvider>
        </>
    )
}

export default AppContainer
